import { cloneElement, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type FocusEvent, type PointerEvent as RPointerEvent, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Ayuda contextual (regla del dueño, 2026-10-19): toda funcionalidad explica en la interfaz
 * CÓMO se usa y POR QUÉ funciona así. Burbuja corporativa (fondo azul noche, 11.5px) anclada
 * al disparador:
 *  - sin `children` → ícono ⓘ discreto (botón) junto a la etiqueta;
 *  - con `children` → el propio elemento (chip, enlace) es el disparador.
 * Desktop: se muestra al pasar el mouse o al enfocar con teclado. Móvil: se abre con tap y se
 * cierra con tap fuera, con el mismo tap, con Escape o al hacer scroll.
 * Accesible: `aria-describedby` apunta al texto (siempre presente, sr-only) y la burbuja
 * visible lleva `role="tooltip"`. Se renderiza en un portal con `position: fixed` para que no
 * la recorten los contenedores con scroll (tablas, modales) y queda dentro del viewport.
 */
export function Ayuda({
  texto,
  children,
  toque = true,
  className = '',
}: {
  texto: string;
  /** Disparador propio (chip, enlace…). Si es un elemento, recibe `aria-describedby`. */
  children?: ReactNode;
  /** Si es false, el tap/click NO alterna la burbuja (p. ej. enlaces: el click navega). */
  toque?: boolean;
  className?: string;
}) {
  const id = useId();
  const ancla = useRef<HTMLSpanElement>(null);
  const [abierto, setAbierto] = useState(false);
  const [hover, setHover] = useState(false);
  const [foco, setFoco] = useState(false);
  const [pos, setPos] = useState<{ left: number; top?: number; bottom?: number; ancho: number; flecha: number; arriba: boolean } | null>(null);

  const visible = abierto || hover || foco;

  // Posición fija, centrada bajo/sobre el disparador y acotada al viewport (12px de margen).
  const calcular = useCallback(() => {
    const r = ancla.current?.getBoundingClientRect();
    if (!r) return;
    const margen = 12;
    const ancho = Math.min(272, window.innerWidth - margen * 2);
    const centro = r.left + r.width / 2;
    const left = Math.max(margen, Math.min(centro - ancho / 2, window.innerWidth - margen - ancho));
    const arriba = r.top > 170; // si no hay espacio arriba, se abre hacia abajo
    setPos({
      left,
      ancho,
      flecha: Math.max(14, Math.min(centro - left, ancho - 14)),
      arriba,
      ...(arriba ? { bottom: window.innerHeight - r.top + 8 } : { top: r.bottom + 8 }),
    });
  }, []);

  // Posición antes del paint (sin parpadeo ni render intermedio sin burbuja).
  useLayoutEffect(() => { if (visible) calcular(); }, [visible, calcular]);

  useEffect(() => {
    if (!visible) return;
    const cerrar = () => { setAbierto(false); setHover(false); setFoco(false); };
    const fuera = (e: PointerEvent) => { if (!ancla.current?.contains(e.target as Node)) cerrar(); };
    const tecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); cerrar(); } // no cierra el modal de fondo
    };
    document.addEventListener('pointerdown', fuera);
    document.addEventListener('keydown', tecla);
    document.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('pointerdown', fuera);
      document.removeEventListener('keydown', tecla);
      document.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [visible, calcular]);

  // Hover solo con mouse real (en táctil el tap manda, si no el primer tap abre y el segundo no cierra).
  const props = {
    onPointerEnter: (e: RPointerEvent) => { if (e.pointerType === 'mouse') setHover(true); },
    onPointerLeave: (e: RPointerEvent) => { if (e.pointerType === 'mouse') setHover(false); },
    onFocus: (e: FocusEvent) => { if ((e.target as HTMLElement).matches(':focus-visible')) setFoco(true); },
    onBlur: () => setFoco(false),
    onClick: toque ? () => setAbierto((v) => !v) : undefined,
  };

  const burbuja = visible && pos
    ? createPortal(
        <span
          role="tooltip"
          aria-hidden="true"
          style={{ left: pos.left, top: pos.top, bottom: pos.bottom, width: pos.ancho }}
          className="anim-entrada pointer-events-none fixed z-[60] rounded-xl bg-azul-noche px-3 py-2 text-[11.5px] font-normal leading-relaxed text-white shadow-xl ring-1 ring-black/10"
        >
          {texto}
          <span
            aria-hidden="true"
            style={{ left: pos.flecha }}
            className={`absolute h-2 w-2 -translate-x-1/2 rotate-45 bg-azul-noche ${pos.arriba ? 'top-full -translate-y-1' : 'bottom-full translate-y-1'}`}
          />
        </span>,
        document.body,
      )
    : null;

  const disparador = isValidElement(children)
    ? cloneElement(children as ReactElement<{ 'aria-describedby'?: string }>, { 'aria-describedby': id })
    : children ?? (
        <button
          type="button"
          aria-label="Más información"
          aria-describedby={id}
          aria-expanded={visible}
          className={`inline-flex h-5 w-5 items-center justify-center rounded-full transition-colors duration-150 hover:text-azul-profundo focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-azul-profundo ${visible ? 'text-azul-profundo' : 'text-gris/60'}`}
        >
          <svg viewBox="0 0 24 24" className="h-[15px] w-[15px]" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="9.5" /><path d="M12 16.5v-5M12 7.75h.01" />
          </svg>
        </button>
      );

  return (
    <span ref={ancla} className={`relative inline-flex items-center align-middle ${children ? '' : 'ml-1'} ${className}`} {...props}>
      {disparador}
      <span id={id} className="sr-only">{texto}</span>
      {burbuja}
    </span>
  );
}
